import React, { useState } from 'react';
import { BilingualText } from '../components/BilingualText';
import { ArrowLeft, CheckCircle2, XCircle, Clock, Calendar as CalendarIcon, Filter, Search, MoreVertical, AlertCircle, ChevronRight } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
interface ApprovalRequest {
  id: string;
  appointmentNo: string;
  supplier: string;
  shipment: string;
  warehouse: string;
  date: string;
  time: string;
  capacity: string;
  priority: 'High' | 'Normal' | 'Low';
  status: 'Pending';
}
const initialRequests: ApprovalRequest[] = [{
  id: '1',
  appointmentNo: 'APP-2026-001',
  supplier: 'Global Logistics',
  shipment: 'ASN-98231',
  warehouse: 'Riyadh Main',
  date: 'July 12, 2026',
  time: '09:30 AM',
  capacity: '42 Cartons',
  priority: 'High',
  status: 'Pending'
}, {
  id: '2',
  appointmentNo: 'APP-2026-005',
  supplier: 'FastTrack Trading',
  shipment: 'ASN-98235',
  warehouse: 'Jeddah Port',
  date: 'July 12, 2026',
  time: '11:00 AM',
  capacity: '15 Cartons',
  priority: 'Normal',
  status: 'Pending'
}, {
  id: '3',
  appointmentNo: 'APP-2026-012',
  supplier: 'Prime Goods Ltd',
  shipment: 'ASN-98240',
  warehouse: 'Riyadh Main',
  date: 'July 13, 2026',
  time: '08:00 AM',
  capacity: '88 Cartons',
  priority: 'Normal',
  status: 'Pending'
}, {
  id: '4',
  appointmentNo: 'APP-2026-018',
  supplier: 'Desert Express',
  shipment: 'ASN-98248',
  warehouse: 'Dammam Hub',
  date: 'July 13, 2026',
  time: '01:30 PM',
  capacity: '56 Cartons',
  priority: 'Low',
  status: 'Pending'
}];
export function ApprovalQueue() {
  const [requests, setRequests] = useState(initialRequests);
  const navigate = useNavigate();
  return <div className="min-h-screen bg-slate-50 pb-24" data-id="element-201">
      <header className="bg-white px-4 py-6 border-b border-slate-200 flex items-center justify-between sticky top-0 z-10 lg:px-8" data-id="element-202">
        <div className="flex items-center gap-4" data-id="element-203">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 text-slate-600 hover:bg-slate-50 rounded-full transition-colors" data-id="element-204">
            <ArrowLeft size={24} data-id="element-205" />
          </button>
          <BilingualText en="Approval Queue" ar="قائمة الانتظار للموافقة" className="text-xl font-bold" data-id="element-206" />
        </div>
        <div className="flex gap-2" data-id="element-207">
          <button className="btn-secondary py-2 px-4 text-sm hidden sm:flex" data-id="element-208">
            <span data-id="element-209">Bulk Approve</span>
          </button>
        </div>
      </header>

      <main className="p-4 md:p-8 max-w-7xl mx-auto space-y-6" data-id="element-210">
        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4" data-id="element-211">
          <div className="flex-1 relative" data-id="element-212">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} data-id="element-213" />
            <input type="text" placeholder="Search by Appointment, Supplier or ASN..." className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-xl shadow-sm" data-id="element-214" />
          </div>
          <div className="flex gap-2" data-id="element-215">
            <button className="btn-secondary py-3 px-4" data-id="element-216">
              <Filter size={18} data-id="element-217" />
              <span data-id="element-218">Filters</span>
            </button>
          </div>
        </div>

        {/* Mobile View: Cards */}
        <div className="grid grid-cols-1 gap-4 lg:hidden" data-id="element-219">
          {requests.map(req => <div key={req.id} className="card p-4 space-y-4" data-id="element-220">
              <div className="flex justify-between items-start" data-id="element-221">
                <div data-id="element-222">
                  <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-223">
                    {req.appointmentNo}
                  </div>
                  <div className="font-bold text-slate-800" data-id="element-224">{req.supplier}</div>
                </div>
                <PriorityBadge priority={req.priority} data-id="element-225" />
              </div>

              <div className="grid grid-cols-2 gap-y-3 py-3 border-y border-slate-50" data-id="element-226">
                <InfoItem label="Shipment" value={req.shipment} data-id="element-227" />
                <InfoItem label="Warehouse" value={req.warehouse} data-id="element-228" />
                <InfoItem label="Date" value={req.date} data-id="element-229" />
                <InfoItem label="Time" value={req.time} data-id="element-230" />
              </div>

              <div className="flex gap-2 pt-2" data-id="element-231">
                <button className="flex-1 bg-green-50 text-green-600 py-2.5 rounded-lg font-bold text-sm flex items-center justify-center gap-2 hover:bg-green-100 transition-colors" data-id="element-232">
                  <CheckCircle2 size={16} data-id="element-233" />
                  Approve
                </button>
                <button className="flex-1 bg-red-50 text-red-600 py-2.5 rounded-lg font-bold text-sm flex items-center justify-center gap-2 hover:bg-red-100 transition-colors" data-id="element-234">
                  <XCircle size={16} data-id="element-235" />
                  Reject
                </button>
              </div>
            </div>)}
        </div>

        {/* Desktop View: Table */}
        <div className="hidden lg:block card overflow-hidden" data-id="element-236">
          <table className="w-full text-left" data-id="element-237">
            <thead className="bg-slate-50 text-slate-400 text-xs uppercase tracking-wider" data-id="element-238">
              <tr data-id="element-239">
                <th className="px-6 py-4 font-bold" data-id="element-240">Appointment</th>
                <th className="px-6 py-4 font-bold" data-id="element-241">Supplier / Shipment</th>
                <th className="px-6 py-4 font-bold" data-id="element-242">Warehouse</th>
                <th className="px-6 py-4 font-bold" data-id="element-243">Requested Schedule</th>
                <th className="px-6 py-4 font-bold" data-id="element-244">Capacity</th>
                <th className="px-6 py-4 font-bold" data-id="element-245">Priority</th>
                <th className="px-6 py-4 font-bold text-right" data-id="element-246">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-247">
              {requests.map(req => <tr key={req.id} className="hover:bg-slate-50 transition-colors group" data-id="element-248">
                  <td className="px-6 py-4 font-bold text-slate-700" data-id="element-249">
                    {req.appointmentNo}
                  </td>
                  <td className="px-6 py-4" data-id="element-250">
                    <div className="font-bold text-slate-800" data-id="element-251">
                      {req.supplier}
                    </div>
                    <div className="text-xs text-slate-400" data-id="element-252">{req.shipment}</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600" data-id="element-253">
                    {req.warehouse}
                  </td>
                  <td className="px-6 py-4" data-id="element-254">
                    <div className="text-sm font-bold text-slate-700" data-id="element-255">
                      {req.date}
                    </div>
                    <div className="text-xs text-slate-400 flex items-center gap-1" data-id="element-256">
                      <Clock size={10} data-id="element-257" />
                      {req.time}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm font-medium text-slate-600" data-id="element-258">
                    {req.capacity}
                  </td>
                  <td className="px-6 py-4" data-id="element-259">
                    <PriorityBadge priority={req.priority} data-id="element-260" />
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-261">
                    <div className="flex items-center justify-end gap-2" data-id="element-262">
                      <button className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-all" title="Approve" data-id="element-263">
                        <CheckCircle2 size={18} data-id="element-264" />
                      </button>
                      <button className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-all" title="Reject" data-id="element-265">
                        <XCircle size={18} data-id="element-266" />
                      </button>
                      <button className="p-2 text-slate-400 hover:text-slate-600 rounded-lg" data-id="element-267">
                        <MoreVertical size={18} data-id="element-268" />
                      </button>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </main>
    </div>;
}
function PriorityBadge({
  priority
}: {
  priority: 'High' | 'Normal' | 'Low';
}) {
  const colors = {
    High: 'bg-red-100 text-red-700',
    Normal: 'bg-blue-100 text-blue-700',
    Low: 'bg-slate-100 text-slate-600'
  };
  return <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase ${colors[priority]}`} data-id="element-269">
      {priority}
    </span>;
}
function InfoItem({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div data-id="element-270">
      <div className="text-[10px] font-bold text-slate-400 uppercase" data-id="element-271">
        {label}
      </div>
      <div className="text-sm font-bold text-slate-700" data-id="element-272">{value}</div>
    </div>;
}